import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { ConfirmationModalIcon } from '@/shared/components/confirmation/ConfirmationModalIcon'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from '@/shared/components/confirmation/ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'
import { CampaignCalendarPickerIcon } from './CampaignCalendarPickerIcon'
import {
  formatCampaignDurationRange,
  isoToDateOnlyUtc,
} from '../lib/campaign-duration-display'
import {
  campaignDurationEndDateSchema,
  END_DATE_PAST_MESSAGE,
} from '../lib/campaign-duration-schema'
import { todayDateOnlyLocal } from '../lib/campaign-schema'

dayjs.extend(utc)

const FORM_DATE_FMT = 'YYYY-MM-DD'
const DISPLAY_DATE_FMT = 'D MMM YYYY'

type Props = Readonly<{
  open: boolean
  startDate: string | null
  endDate: string | null
  saving: boolean
  onClose: () => void
  onSave: (endDateIso: string) => void
}>

const fieldLabelSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#0f172a',
  mb: '8px',
  display: 'block',
  textAlign: 'left',
} as const

const disabledFieldSx = {
  height: '46px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  bgcolor: '#f6f6f6',
  display: 'flex',
  alignItems: 'center',
  px: '13px',
} as const

function utcDayFromFormString(s: string | undefined): Dayjs | null {
  if (!s) return null
  const d = dayjs.utc(s, FORM_DATE_FMT, true)
  return d.isValid() ? d.startOf('day') : null
}

function utcDayStringFromPickerValue(v: Dayjs | null): string {
  if (!v?.isValid()) return ''
  return v.utc().startOf('day').format(FORM_DATE_FMT)
}

export function EditCampaignDurationDialog({
  open,
  startDate,
  endDate,
  saving,
  onClose,
  onSave,
}: Props) {
  const startDateOnly = isoToDateOnlyUtc(startDate)
  const [endDateOnly, setEndDateOnly] = useState(isoToDateOnlyUtc(endDate))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setEndDateOnly(isoToDateOnlyUtc(endDate))
    setError(null)
  }, [open, endDate])

  const todayUtc = useMemo(
    () => dayjs.utc(todayDateOnlyLocal(), FORM_DATE_FMT, true).startOf('day'),
    [],
  )
  const startUtc = utcDayFromFormString(startDateOnly)
  const minDate = startUtc?.isAfter(todayUtc) ? startUtc : todayUtc

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleUpdate = () => {
    const parsed = campaignDurationEndDateSchema(startDateOnly).safeParse({
      endDate: endDateOnly,
    })
    if (!parsed.success) {
      const message = parsed.error.issues[0]?.message ?? END_DATE_PAST_MESSAGE
      setError(message)
      return
    }
    setError(null)
    onSave(`${parsed.data.endDate}T00:00:00.000Z`)
  }

  return (
    <ConfirmationModalShell
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={saving}
      dialogTestId="edit-campaign-duration-dialog"
      ariaLabelledBy="edit-campaign-duration-headline"
      ariaDescribedBy="edit-campaign-duration-body"
    >
      <ConfirmationModalLayout>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={saving}
            data-testid="edit-campaign-duration-close"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 28,
              height: 28,
              color: '#64748b',
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Box sx={{ pt: '32px' }}>
          <ConfirmationModalIcon variant="edit" />
        </Box>

        <Box
          id="edit-campaign-duration-body"
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography
            id="edit-campaign-duration-headline"
            component="h2"
            sx={confirmationHeadlineSx}
          >
            Edit Campaign Duration
          </Typography>
          <Typography sx={{ ...confirmationBodySx, maxWidth: 388, textAlign: 'center' }}>
            You can update the Campaign End Date. The original Campaign Start Date cannot
            be changed after the campaign has launched.
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            px: '24px',
            pt: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box>
            <Typography component="label" sx={fieldLabelSx}>
              Start Date
            </Typography>
            <Box sx={disabledFieldSx} data-testid="edit-campaign-duration-start">
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#94a3b8',
                }}
              >
                {formatCampaignDurationRange(startDate, null)}
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <Typography
                component="label"
                htmlFor="edit-campaign-duration-end"
                sx={{ ...fieldLabelSx, color: '#23262f' }}
              >
                End Date
              </Typography>
              <DatePicker
                format={DISPLAY_DATE_FMT}
                value={utcDayFromFormString(endDateOnly)}
                onChange={(v) => {
                  setEndDateOnly(utcDayStringFromPickerValue(v))
                  setError(null)
                }}
                minDate={minDate}
                disabled={saving}
                desktopModeMediaQuery="@media (min-width: 0px)"
                slots={{ openPickerIcon: CampaignCalendarPickerIcon }}
                slotProps={{
                  openPickerIcon: {
                    sx: { fontSize: 22, color: '#4a5565' },
                  },
                  textField: {
                    id: 'edit-campaign-duration-end',
                    fullWidth: true,
                    error: Boolean(error),
                    helperText: error,
                    inputProps: {
                      'data-testid': 'edit-campaign-duration-end',
                    },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        height: 46,
                        borderRadius: '10px',
                        bgcolor: '#ffffff',
                        fontSize: '14px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            width: '100%',
            px: 3,
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={saving}
            data-testid="edit-campaign-duration-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving || !endDateOnly}
            data-testid="edit-campaign-duration-update"
            sx={{ ...confirmationPrimaryButtonSx, minWidth: 141 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Update'}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
